import * as React from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import CssBaseline from '@mui/material/CssBaseline';
import PanasonicLogo from './../../assets/logo/syslogin_panasonic_logo.png'
import LeftNav from './../LeftNav'
import TopBar from '../TopBar';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { appTypes } from '../../redux/types';
import useEnsureDefaultTenant from '../../hooks/useEnsureDefaultTenant';
import { getDefaultLanding } from '../../config/navigation';

const expandedDrawerWidth = 280;
const collapsedDrawerWidth = 72;
const drawerOffset = 8;
const HOVER_OPEN_DELAY_MS = 50;
const HOVER_CLOSE_DELAY_MS = 180;

export default function SideBar({ children }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    useEnsureDefaultTenant();
    const [isHovered, setIsHovered] = React.useState(false);
    const openTimerRef = React.useRef(null);
    const closeTimerRef = React.useRef(null);

    const open = isHovered;
    const sidebarWidth = (open ? expandedDrawerWidth : collapsedDrawerWidth) + drawerOffset;

    const clearHoverTimers = React.useCallback(() => {
        if (openTimerRef.current) {
            clearTimeout(openTimerRef.current);
            openTimerRef.current = null;
        }
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    }, []);

    const handleDrawerEnter = React.useCallback(() => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        if (isHovered) return;
        openTimerRef.current = setTimeout(() => {
            setIsHovered(true);
        }, HOVER_OPEN_DELAY_MS);
    }, [isHovered]);

    const handleDrawerLeave = React.useCallback(() => {
        if (openTimerRef.current) {
            clearTimeout(openTimerRef.current);
            openTimerRef.current = null;
        }
        closeTimerRef.current = setTimeout(() => {
            setIsHovered(false);
        }, HOVER_CLOSE_DELAY_MS);
    }, []);

    React.useEffect(() => () => clearHoverTimers(), [clearHoverTimers]);

    const handleLogoClick = () => {
        const landing = getDefaultLanding();
        navigate(landing.path);
        dispatch({ type: appTypes.SELECT_LEFT_MENU, payload: landing.menuId });
    };

    return (
        <Box sx={{ display: 'flex', overflowX: 'hidden', minHeight: '100vh' }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                sx={{
                    backgroundColor: '#ffffff',
                    width: '100%',
                    color: 'text.primary',
                    boxShadow: '0 1px 2px rgba(15,23,42,0.08)',
                    borderBottom: '1px solid #e2e8f0',
                    zIndex: (theme) => theme.zIndex.drawer + 2,
                }}
            >
                <Toolbar>
                    <Box
                        component="img"
                        src={PanasonicLogo}
                        onClick={handleLogoClick}
                        sx={{ width: '170px', cursor: 'pointer' }}
                    />
                    <Typography
                        variant="subtitle2"
                        sx={{
                            ml: 2,
                            color: "text.secondary",
                            letterSpacing: 0.4,
                            display: { xs: "none", md: "block" },
                        }}
                    >
                        Identity Platform Admin
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <TopBar />
                </Toolbar>
            </AppBar>

            <Box
                aria-hidden
                sx={{
                    width: sidebarWidth,
                    flexShrink: 0,
                    transition: 'width 200ms ease',
                }}
            />

            <Drawer
                variant="permanent"
                open
                onMouseEnter={handleDrawerEnter}
                onMouseLeave={handleDrawerLeave}
                PaperProps={{
                    sx: {
                        backgroundColor: "#f8fafc",
                        color: "#1e293b",
                        border: "1px solid #e2e8f0",
                        width: open ? expandedDrawerWidth : collapsedDrawerWidth,
                        boxShadow: open
                            ? "0 10px 28px rgba(15, 23, 42, 0.18)"
                            : "0 2px 8px rgba(15, 23, 42, 0.06)",
                        transition: "width 200ms ease, box-shadow 200ms ease",
                        overflowX: "hidden",
                        overflowY: "hidden",
                        top: 72,
                        height: "calc(100% - 80px)",
                        left: drawerOffset,
                        borderRadius: "20px",
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                    },
                }}
            >
                <LeftNav open={open} />
            </Drawer>

            <Box
                component="main"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    minWidth: 0,
                    p: { xs: 2, md: 3 },
                    mt: 8,
                    overflowX: "clip",
                    contain: 'layout',
                }}
            >
                {children}
            </Box>
        </Box>
    );
}
